// @flow
import React, { useEffect, useCallback, useState } from 'react';
import { withFlux } from '@homie/flux';
import { get } from 'lodash';
import swal from 'sweetalert2';
import { PrimaryButton } from 'styles/Buttons';
import OwnerReportActions from 'modules/external_tenant/api/actions/OwnerReportActions';
import OwnerReportStore from 'modules/external_tenant/api/stores/OwnerReportStore';
import HomeActions from 'modules/external_tenant/api/actions/HomeActions';
import ReportScore from './ReportScore';
import {
  StyledHeader,
  Paragraph,
  TenantName,
  ReportContainerInner,
  ScoreCategoryLabel,
  ScoreComparisonLabel,
  SummaryFieldLabel,
  SummaryFieldData,
  SummaryField,
  ReportDetailContainer,
  ReportScoreContainer,
  SummaryContainer,
  DetailField,
  DetailFieldLabel,
  DetailFieldData,
  DetailTitle,
  DetailContainer,
  ValidatedByContainer,
  ValidatedByParagraph
} from './styles';

const viewPortIsMobile = () => window.innerWidth <= 767;
const getScoreSize = isMobile => (!isMobile ? 'big' : 'small');

type FullReportProps = {
  applicationId: string,
  userId: string,
  report: any,
  history: any,
  showLoadingProcess: (msg: string) => null,
  hideLoadingProcess: () => null
};

const FullReport = ({
  applicationId,
  userId,
  report,
  history,
  showLoadingProcess,
  hideLoadingProcess
}: FullReportProps) => {
  const [isMobile, setIsMobile] = useState(viewPortIsMobile());
  const handleWindowResize = useCallback(() => {
    setIsMobile(viewPortIsMobile());
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  useEffect(() => {
    showLoadingProcess('Cargando reporte...');
    OwnerReportActions.fetchReportFull(userId, applicationId)
      .then(() => {
        hideLoadingProcess();
      })
      .catch(e => {
        hideLoadingProcess();
        swal({
          title: '¡Ha ocurrido un error!',
          text:
            (e.responseJson && e.responseJSON.message) ||
            'Ocurrió un error desconocido mientras se cargaban los datos del reporte',
          type: 'error'
        });
      });
  }, []);

  const createHome = () => {
    showLoadingProcess('Cargando datos...');
    HomeActions.createHome(applicationId)
      .then(homeId => {
        hideLoadingProcess();
        history.push(
          `/inquilinos-externos/aplicaciones/${applicationId}/registro/inmueble/${homeId}/precio`
        );
      })
      .catch(e => {
        hideLoadingProcess();
        swal({
          title: '¡Ha ocurrido un error!',
          text:
            (e.responseJson && e.responseJSON.message) ||
            'Ocurrió un error desconocido mientras se cargaban los datos',
          type: 'error'
        });
      });
  };

  const income = get(
    report,
    'home_application.home_application_income.verified_income',
    0
  );
  const formattedIncome = income !== null ? income.toLocaleString() : '0.00';

  const home = get(report, 'home_application.home');

  const showRentButtonDesktop = !isMobile && get(home, 'price', 0) === 0;
  const showRentButtonMobile = isMobile && get(home, 'price', 0) === 0;

  return (
    <ReportContainerInner>
      <ReportScoreContainer>
        <div>
          <StyledHeader marginBottom={8} type="primary">
            Renta tu departamento con Homie
          </StyledHeader>
          <TenantName>
            {get(report, 'home_application.user.full_name', '')}
          </TenantName>
          <Paragraph>
            Fue aprobado por Homie como buen inquilino para vivir en tu
            inmueble.
          </Paragraph>
        </div>
        <ReportScore
          score={get(report, 'homie_score', 0)}
          size={getScoreSize(isMobile)}
        />
        <ScoreCategoryLabel>
          <strong>Este Puntaje es {get(report, 'risk', '')}</strong>
        </ScoreCategoryLabel>
        <ScoreComparisonLabel>
          Rango superior al 22% de los inquilinos en México
        </ScoreComparisonLabel>
        {showRentButtonDesktop && (
          <div style={{ textAlign: 'center' }}>
            <PrimaryButton style={{ width: 300 }} onClick={createHome}>
              Réntale a {get(report, 'home_application.user.name', '')}
            </PrimaryButton>
          </div>
        )}
      </ReportScoreContainer>
      <ReportDetailContainer>
        <SummaryContainer>
          <SummaryField>
            <SummaryFieldLabel>Identificación otorgada:</SummaryFieldLabel>
            <SummaryFieldData>
              {get(report, 'home_application.identification_granted', false)
                ? 'Verificada'
                : 'No verificada'}
            </SummaryFieldData>
          </SummaryField>
          <SummaryField>
            <SummaryFieldLabel>Nivel socioeconómico:</SummaryFieldLabel>
            <SummaryFieldData>
              {get(report, 'home_application.socioeconomic_value', 'NA')}
            </SummaryFieldData>
          </SummaryField>
          <SummaryField>
            <SummaryFieldLabel>Listas negras:</SummaryFieldLabel>
            <SummaryFieldData>
              {get(
                report,
                'home_application.home_application_income.blacklisted',
                false
              )
                ? 'Si'
                : 'No'}
            </SummaryFieldData>
          </SummaryField>
          <SummaryField>
            <SummaryFieldLabel>
              Prevención de lavado de dinero:
            </SummaryFieldLabel>
            <SummaryFieldData>
              {get(report, 'home_application.money_laundering_prevension', '')}
            </SummaryFieldData>
          </SummaryField>
          <SummaryField>
            <SummaryFieldLabel>Redes sociales vinculadas:</SummaryFieldLabel>
            <SummaryFieldData>
              {get(report, 'home_application.social_networks', '')}
            </SummaryFieldData>
          </SummaryField>
        </SummaryContainer>
        <DetailContainer>
          <DetailTitle>Datos del inquilino</DetailTitle>
          <DetailField>
            <DetailFieldLabel>Empleo:</DetailFieldLabel>
            <DetailFieldData>
              <strong>
                {get(
                  report,
                  'home_application.home_application_income.position',
                  ''
                )}{' '}
                |{' '}
                {get(
                  report,
                  'home_application.home_application_income.company.business_name',
                  ''
                )}
              </strong>
            </DetailFieldData>
          </DetailField>
          <DetailField>
            <DetailFieldLabel>Nivel de estudios:</DetailFieldLabel>
            <DetailFieldData>
              <strong>
                {get(report, 'home_application.user.degree', '')} |{' '}
                {get(report, 'home_application.user.college', '')}
              </strong>
            </DetailFieldData>
          </DetailField>
          <DetailField>
            <DetailFieldLabel>Ingresos verificados:</DetailFieldLabel>
            <DetailFieldData>
              <strong>$ {formattedIncome}</strong>
            </DetailFieldData>
          </DetailField>
          <DetailField>
            <DetailFieldLabel>Mail:</DetailFieldLabel>
            <DetailFieldData>
              <strong>{get(report, 'home_application.user.email', '')}</strong>
            </DetailFieldData>
          </DetailField>
          <DetailField>
            <DetailFieldLabel>Célular:</DetailFieldLabel>
            <DetailFieldData>
              <strong>
                {get(report, 'home_application.user.mobile_phone', '')}
              </strong>
            </DetailFieldData>
          </DetailField>
          <DetailField>
            <DetailFieldLabel>Referencia:</DetailFieldLabel>
            <DetailFieldData>
              <strong>
                {get(report, 'home_application.references[0].name')}{' '}
                {get(report, 'home_application.references[0].last_name')}{' '}
                {get(report, 'home_application.references[0].second_last_name')}
              </strong>
              <div>
                {get(
                  report,
                  'home_application.references[0].address.street',
                  ''
                )}
                ,{' '}
                {get(
                  report,
                  'home_application.references[0].address.ext_number',
                  ''
                )}
                ,{' '}
                {get(
                  report,
                  'home_application.references[0].address.neighborhood',
                  ''
                )}
                ,{' '}
                {get(
                  report,
                  'home_application.references[0].address.state',
                  ''
                )}
                ,{' '}
                {get(
                  report,
                  'home_application.references[0].address.country',
                  ''
                )}
              </div>
            </DetailFieldData>
          </DetailField>
        </DetailContainer>
      </ReportDetailContainer>

      {isMobile && (
        <React.Fragment>
          <ValidatedByContainer>
            <DetailTitle>
              <img src="/assets/external-tenant/h.png" width={37} height={37} />
              Reporte de Inquilino
            </DetailTitle>
            <ValidatedByParagraph>
              Calculado con Homie Technology que tiene un rango de 0 a 1500
            </ValidatedByParagraph>
            <ValidatedByParagraph>
              Nuestra tecnología nos permite rentar tu inmueble al mejor
              inquilino, sin ningún riesgo y en tiempo record.
            </ValidatedByParagraph>
            <p style={{ textAlign: 'center', padding: '33px 0' }}>
              <img
                src="/assets/external-tenant/homie-tech-gris.png"
                width={124}
                height={33}
              />
            </p>
            <div style={{ textAlign: 'center' }}>
              <ValidatedByParagraph>
                <strong>
                  ¡{get(report, 'home_application.user.name', '')} es un
                  inquilino confiable, réntale tu inmueble en menos de 24 hrs!
                </strong>
              </ValidatedByParagraph>
              {showRentButtonMobile && (
                <PrimaryButton style={{ width: 300 }} onClick={createHome}>
                  Réntale a {get(report, 'home_application.user.name', '')}
                </PrimaryButton>
              )}
            </div>
          </ValidatedByContainer>
        </React.Fragment>
      )}
    </ReportContainerInner>
  );
};

export default withFlux([OwnerReportStore], () => ({
  report: OwnerReportStore.getReportFull()
}))(FullReport);
